import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import auth_reducer from './auth_slice';
import client_reducer from './client_slice';
import ui_reducer from './ui_slice';

/**
 * Root reducer combining all slice reducers
 */
const root_reducer = combineReducers({
  auth: auth_reducer,
  client: client_reducer,
  ui: ui_reducer,
});

/**
 * Configure Redux store with middleware and dev tools
 */
export const store = configureStore({
  reducer: root_reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['ui/show_toast'],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.timestamp'],
        // Ignore these paths in the state
        ignoredPaths: ['ui.toast.timestamp'],
      },
    }),
  devTools: import.meta.env.MODE !== 'production',
});

/**
 * Infer the `RootState` and `AppDispatch` types from the store itself
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Pre-typed hooks for use throughout the app
 * Use these instead of plain `useDispatch` and `useSelector`
 */
export const use_app_dispatch = () => useDispatch<AppDispatch>();
export const use_app_selector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Export action creators and selectors for easy access
 */
export * from './auth_slice';
export * from './client_slice';
export * from './ui_slice';